import BaseButton from 'base-components/BaseButton/BaseButton';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { HeaderRenderer, HeaderRendererProps, SortColumn } from 'react-data-grid';
import { Popover, PopoverBody } from 'reactstrap';
import 'scss/Main.scss';
import BaseDataGridTooltipedFormatter from './BaseDataGridTooltipedFormatter';

const BaseDataGridHeaderRenderer = <TableType, TSummaryRow>({
    columnsForSorting,
    sortColumns,
    onSortColumnsChange,
    headerProps,
    tooltipText,
    tooltipAlwaysShow,
}: React.HTMLAttributes<HTMLDivElement> & {
    columnsForSorting?: {
        key: string;
        name: string;
    }[];
    sortColumns: SortColumn[];
    onSortColumnsChange: Dispatch<SetStateAction<SortColumn[]>>;
    headerProps: HeaderRendererProps<TableType, TSummaryRow>;
    tooltipText?: string;
    tooltipAlwaysShow?: boolean;
}) => {
    const headerRef = useRef(null);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const toggle = () => setPopoverOpen((prev) => !prev);

    const onSort = (columnKey: string) => {
        const { sortDescendingFirst } = headerProps.column;

        onSortColumnsChange((prev) => {
            const sortIndex = prev.findIndex(
                (sort) => columnsForSorting?.some((x) => x.key === sort.columnKey)
            );

            if (sortIndex < 0 || prev[sortIndex].columnKey !== columnKey) {
                const nextSort: SortColumn = {
                    columnKey: columnKey,
                    direction: sortDescendingFirst ? 'DESC' : 'ASC',
                };

                return [nextSort];
            } else {
                const prevSortDirection = prev[sortIndex].direction;

                let nextSortColumn: SortColumn | undefined;
                if (
                    (sortDescendingFirst && prevSortDirection === 'DESC') ||
                    (!sortDescendingFirst && prevSortDirection === 'ASC')
                ) {
                    nextSortColumn = {
                        columnKey: columnKey,
                        direction: prevSortDirection === 'ASC' ? 'DESC' : 'ASC',
                    };
                }
                return nextSortColumn ? [nextSortColumn] : [];
            }
        });
    };

    return (
        <>
            <div ref={headerRef}>
                <BaseDataGridTooltipedFormatter
                    tooltipText={tooltipText ?? headerProps.column.name}
                    overflownElementClassName={
                        headerProps.column.sortable ? 'rdg-header-sort-name' : undefined
                    }
                    showAlways={tooltipAlwaysShow}
                >
                    <HeaderRenderer
                        {...headerProps}
                        onSort={columnsForSorting ? toggle : headerProps.onSort}
                        sortDirection={
                            columnsForSorting
                                ? sortColumns.find(
                                      (item) =>
                                          columnsForSorting?.some((x) => x.key === item.columnKey)
                                  )?.direction
                                : headerProps.sortDirection
                        }
                    />
                </BaseDataGridTooltipedFormatter>
            </div>

            {columnsForSorting && (
                <Popover
                    target={headerRef}
                    placement="bottom-start"
                    container={headerRef}
                    isOpen={popoverOpen}
                    trigger="legacy"
                    toggle={toggle}
                    className="base-header-renderer"
                >
                    <PopoverBody>
                        {columnsForSorting &&
                            columnsForSorting.map((col) => (
                                <div key={col.key}>
                                    <BaseButton onPointerDownCapture={() => onSort(col.key)}>
                                        {col.name}
                                    </BaseButton>
                                </div>
                            ))}
                    </PopoverBody>
                </Popover>
            )}
        </>
    );
};

export default BaseDataGridHeaderRenderer;
